import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Briefcase, Clock, UserCheck, BadgeCheck, HeartHandshake, ArrowRight, Phone, Quote } from 'lucide-react';
import { COMPANY, QUALITY_POINTS, REFERENCES } from '../mock/data';
import { useT } from '../i18n/LanguageContext';
import { QUALITY_TRANSLATIONS } from '../i18n/translations';
import { Reveal } from '../hooks/useReveal';
import SEO, { breadcrumbLd } from '../components/SEO';

const iconMap = { Award, Briefcase, Clock, UserCheck, BadgeCheck, HeartHandshake };

const AboutPage = () => {
  const { t, lang } = useT();
  return (
    <div className="bg-white pt-32 pb-0">
      <SEO
        title="Über uns · Armin Achermann · Kundenmaurer aus Seelisberg"
        description="Frachigers Handwerk – Armin Achermann aus Seelisberg (Uri). Einmannbetrieb mit über 20 Jahren Erfahrung, AA+ Qualitätsversprechen und persönlicher Kundenbetreuung in der Zentralschweiz."
        path="/ueber-uns"
        lang={lang}
        jsonLd={[breadcrumbLd([{ name: 'Home', path: '/' }, { name: 'Über uns', path: '/ueber-uns' }])]}
      />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal direction="right">
            <div>
              <div className="text-brand-red uppercase tracking-[0.3em] text-xs mb-3 font-semibold">{t('about.eyebrow')}</div>
              <h1 className="font-display text-5xl md:text-6xl text-brand-blueDark leading-tight mb-6 tracking-tight">
                AA+ <span className="italic text-brand-blue">Qualität</span>.
                <br />
                <span className="text-3xl md:text-4xl text-brand-ink/70 italic">Persönlich · Zuverlässig · Termintreu.</span>
              </h1>
              <p className="text-neutral-700 leading-relaxed text-lg mb-5">{t('about.desc1')}</p>
              <p className="text-neutral-600 leading-relaxed mb-5">{t('about.desc2')}</p>
              <p className="text-neutral-600 leading-relaxed mb-8">{t('about.desc3')}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/kontakt" className="inline-flex items-center justify-center gap-2 bg-brand-blue hover:bg-brand-blueDark text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm transition-colors btn-pulse-dark">
                  {t('hero.cta1')} <ArrowRight className="w-4 h-4" />
                </Link>
                <a href={`tel:${COMPANY.phoneInternational}`} className="inline-flex items-center justify-center gap-2 border-2 border-brand-blue text-neutral-900 hover:bg-white hover:text-neutral-900 px-6 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm transition-colors">
                  <Phone className="w-4 h-4" /> {COMPANY.phone}
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal direction="left" delay={150}>
            <div className="relative">
              <div className="absolute -inset-4 bg-neutral-100 rounded-sm"></div>
              <div className="relative aspect-[4/5] overflow-hidden rounded-sm shadow-2xl">
                <img src={REFERENCES[12].image} alt="Über mich" className="w-full h-full object-cover" />
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
                  <div className="flex items-center gap-3">
                    <img src={COMPANY.logo} alt="Logo" className="h-16 w-16 object-contain" />
                    <div>
                      <div className="font-display text-2xl text-neutral-900">Frachigers Handwerk</div>
                      <div className="text-neutral-700 text-xs uppercase tracking-widest">Armin Achermann · Seelisberg</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="hidden md:flex absolute -bottom-6 -left-6 bg-brand-blueDark text-white p-5 rounded-sm shadow-2xl items-center gap-3 max-w-xs animate-floatY">
                <Quote className="w-8 h-8 text-brand-blueSky flex-shrink-0" />
                <div className="text-sm italic leading-snug">{t('about.motivation')}</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <div className="text-brand-red uppercase tracking-[0.3em] text-xs mb-3 font-semibold">{t('quality.myValues')}</div>
              <h2 className="font-display text-4xl md:text-5xl text-neutral-900 leading-tight">
                {t('quality.title')} <span className="italic text-brand-red">{t('quality.titleEm')}</span>
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-200">
            {QUALITY_POINTS.map((q, i) => {
              const Icon = iconMap[q.icon] || Award;
              const qt = QUALITY_TRANSLATIONS[q.key]?.[lang] || {};
              return (
                <Reveal key={q.id} delay={i * 80}>
                  <div className="bg-neutral-50 p-8 md:p-10 h-full hover:bg-white hover:text-neutral-900 transition-colors group">
                    <div className="flex items-start justify-between mb-5">
                      <div className="w-14 h-14 rounded-sm bg-white group-hover:bg-neutral-50 text-neutral-900 group-hover:text-neutral-900 flex items-center justify-center transition-colors">
                        <Icon className="w-7 h-7" />
                      </div>
                      <span className="font-display text-5xl text-neutral-200 group-hover:text-neutral-900/20 transition-colors">{q.id}</span>
                    </div>
                    <h3 className="font-display text-2xl mb-3">{qt.title}</h3>
                    <p className="text-sm leading-relaxed text-neutral-600 group-hover:text-neutral-600 transition-colors">{qt.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white text-neutral-900 text-center">
        <Reveal>
          <div className="max-w-4xl mx-auto">
            <div className="text-brand-blueLight uppercase tracking-[0.3em] text-xs mb-4 font-bold">{t('about.promise')}</div>
            <p className="font-display text-3xl md:text-5xl leading-tight italic">{t('about.promiseQuote')}</p>
            <div className="mt-10 inline-flex items-center gap-3 text-neutral-800">
              <div className="h-px w-12 bg-brand-gold"></div>
              <span className="font-display text-xl italic">Frachigers Handwerk</span>
              <div className="h-px w-12 bg-brand-gold"></div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default AboutPage;
